import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiCheckSquare,
  FiFileText,
  FiBell,
  FiTrendingUp,
  FiClock,
  FiCheckCircle
} from 'react-icons/fi';
import { taskService } from '../../tasks/services/taskService';
import { noteService } from '../../notes/services/noteService';
import { reminderService } from '../../reminders/services/reminderService';
import { Task, Reminder, DashboardStats } from '../types';
import './Dashboard.css';

interface StatCard {
  title: string;
  value: number;
  icon: JSX.Element;
  color: string;
  link: string;
}

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<DashboardStats>({
    totalTasks: 0,
    completedTasks: 0,
    totalNotes: 0,
    activeReminders: 0
  });
  const [recentTasks, setRecentTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async (): Promise<void> => {
    try {
      const [tasks, notes, reminders] = await Promise.all([
        taskService.getTasks(),
        noteService.getNotes(),
        reminderService.getReminders()
      ]);
      
      const completedCount = tasks.filter((t: Task) => t.completed).length;
      const activeRemindersCount = reminders.filter((r: Reminder) => !r.isCompleted).length;

      setStats({
        totalTasks: tasks.length,
        completedTasks: completedCount,
        totalNotes: notes.length,
        activeReminders: activeRemindersCount
      });

      setRecentTasks(tasks.slice(0, 5));
      setLoading(false);
    } catch (error) {
      console.error('Dashboard verileri yüklenemedi:', error);
      setLoading(false);
    }
  };

  const statCards: StatCard[] = [
    {
      title: t('dashboard.stats.totalTasks'),
      value: stats.totalTasks,
      icon: <FiCheckSquare />,
      color: '#6366f1',
      link: '/tasks'
    },
    {
      title: t('dashboard.stats.completedTasks'),
      value: stats.completedTasks,
      icon: <FiCheckCircle />,
      color: '#10b981',
      link: '/tasks'
    },
    {
      title: t('dashboard.stats.totalNotes'),
      value: stats.totalNotes,
      icon: <FiFileText />,
      color: '#f59e0b',
      link: '/notes'
    },
    {
      title: t('dashboard.stats.totalReminders'),
      value: stats.activeReminders,
      icon: <FiBell />,
      color: '#ef4444',
      link: '/reminders'
    }
  ];

  const getPriorityLabel = (priority: string): string => {
    return t(`tasks.priority.${priority}`);
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="dashboard fade-in">
      <div className="dashboard-header">
        <h1 className="page-title">{t('dashboard.title')}</h1>
        <p className="page-subtitle">{t('dashboard.welcome', { name: '' })}</p>
      </div>

      <div className="stats-grid">
        {statCards.map((stat, index) => (
          <Link 
            to={stat.link} 
            key={index} 
            className="stat-card" 
            style={{ '--card-color': stat.color } as React.CSSProperties}
          >
            <div className="stat-icon">{stat.icon}</div>
            <div className="stat-content">
              <h3 className="stat-title">{stat.title}</h3>
              <p className="stat-value">{stat.value}</p>
            </div>
            <div className="stat-trend">
              <FiTrendingUp />
            </div>
          </Link>
        ))}
      </div>

      <div className="dashboard-content">
        <div className="recent-section">
          <div className="section-header">
            <h2 className="section-title">
              <FiClock /> {t('dashboard.recentActivity')}
            </h2>
            <Link to="/tasks" className="section-link">{t('tasks.title')}</Link>
          </div>
          
          {recentTasks.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📝</div>
              <h3 className="empty-state-title">{t('tasks.noTasks')}</h3>
              <p className="empty-state-text">{t('tasks.createFirst')}</p>
              <Link to="/tasks" className="btn btn-primary">
                {t('tasks.addTask')}
              </Link>
            </div>
          ) : (
            <div className="task-list">
              {recentTasks.map(task => (
                <div key={task._id} className="task-item">
                  <div className={`task-status ${task.completed ? 'completed' : ''}`}>
                    {task.completed ? <FiCheckCircle /> : <FiClock />}
                  </div>
                  <div className="task-info">
                    <h4 className={`task-title ${task.completed ? 'completed' : ''}`}>
                      {task.title}
                    </h4>
                    {task.description && (
                      <p className="task-description">{task.description}</p>
                    )}
                  </div>
                  <span className={`task-priority priority-${task.priority}`}>
                    {getPriorityLabel(task.priority)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="quick-actions">
          <div className="section-header">
            <h2 className="section-title">{t('dashboard.quickActions')}</h2>
          </div>
          <div className="action-buttons">
            <Link to="/tasks" className="action-btn">
              <FiCheckSquare />
              <span>{t('tasks.addTask')}</span>
            </Link>
            <Link to="/notes" className="action-btn">
              <FiFileText />
              <span>{t('notes.addNote')}</span>
            </Link>
            <Link to="/reminders" className="action-btn">
              <FiBell />
              <span>{t('reminders.addReminder')}</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

